import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/homeData';

export default function BasetaoPromoCode() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20">
        <article className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">Promo Codes</span>
            <h1 className="text-2xl md:text-4xl font-heading text-foreground-950 mb-4">BaseTao Promo Code Guide: How to Find & Use Discounts</h1>
            <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">Discover how to find legitimate BaseTao promotional codes and apply them to save on your purchases.</p>
          </div>

          <div className="prose-custom space-y-8">
            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">Where to Find BaseTao Promo Codes</h2>
              <p className="text-foreground-600 leading-relaxed mb-4">Finding legitimate <strong className="text-foreground-900">BaseTao promo codes</strong> requires knowing where to look. The most reliable sources include the official BaseTao website announcements, BaseTao social media channels, and verified promotional emails from the platform.</p>
              <p className="text-foreground-600 leading-relaxed">Be cautious of third-party coupon websites that may list expired or fake codes. Always verify promo codes by checking recent user reports and confirmation before relying on them for your purchase decisions.</p>
            </section>

            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">Types of BaseTao Discounts</h2>
              <div className="space-y-3">
                {[{t:'Shipping Discounts',d:'Reduced or free shipping on orders meeting certain weight or value thresholds. These are common during promotional periods.'},{t:'New User Coupons',d:'First-time BaseTao users may qualify for welcome discounts on their initial order or first shipment.'},{t:'Seasonal Promotions',d:'Major shopping events and holidays often bring temporary discount codes and special offers on the platform.'},{t:'Volume Discounts',d:'Larger hauls with higher total value or weight may qualify for reduced service fees or shipping rates.'}].map((item,i)=>(
                  <div key={i} className="p-4 rounded-xl bg-background-100 border border-background-200/60">
                    <h3 className="font-heading text-sm text-foreground-900 mb-1">{item.t}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{item.d}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">How to Apply a Promo Code</h2>
              <p className="text-foreground-600 leading-relaxed mb-4">Applying a BaseTao promo code is typically straightforward. During the checkout or shipping selection process, look for a promo code or discount code input field. Enter your code exactly as it appears and confirm that the discount has been applied to your total before completing the transaction.</p>
              <p className="text-foreground-600 leading-relaxed">Track any discounts you use in your <Link to="/basetao-spreadsheet/" className="text-primary-600 hover:text-primary-700 font-medium">BaseTao Spreadsheet</Link> by adding a Discount column. This helps you measure your total savings over time. Also check our <Link to="/basetao-coupon/" className="text-primary-600 hover:text-primary-700 font-medium">Coupon Codes page</Link> for the latest verified codes.</p>
            </section>

            <div className="bg-background-100 border border-background-200/70 rounded-xl p-6 mt-10">
              <h3 className="font-heading text-lg text-foreground-900 mb-3">Start Saving Today</h3>
              <p className="text-foreground-600 text-sm mb-4">Browse products on BaseTao Spreadsheet and keep an eye out for available promotions.</p>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-background-50 rounded-xl text-sm font-semibold hover:bg-accent-600 transition-colors duration-200 whitespace-nowrap">Visit BaseTao Spreadsheet <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line text-sm"></i></div></a>
            </div>

            <div className="text-sm text-foreground-400 mt-8 pt-6 border-t border-background-200/60 flex flex-wrap gap-4">
              <Link to="/" className="hover:text-primary-600 transition-colors">&larr; Back to Home</Link>
              <Link to="/basetao-coupon/" className="hover:text-primary-600 transition-colors">Coupon Codes</Link>
              <Link to="/basetao-spreadsheet/" className="hover:text-primary-600 transition-colors">Spreadsheet Guide</Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />

      {/* Schema.org Article */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'BaseTao Promo Code Guide: How to Find & Use Discounts',
            description: 'Discover how to find legitimate BaseTao promotional codes and apply them to save on your purchases.',
            dateModified: '2026-08-10',
            datePublished: '2026-08-10',
            author: {
              '@type': 'Organization',
              name: 'BaseTao Sheet',
            },
            publisher: {
              '@type': 'Organization',
              name: 'BaseTao Sheet',
              logo: {
                '@type': 'ImageObject',
                url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/0dc5bc4081b05862da3d01553c9c9d93.png',
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': 'https://basetaosheet.team/basetao-promo-code/',
            },
          }),
        }}
      />
    </div>
  );
}